import { Component, OnInit, Inject, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { removeBlankStringCells } from '../../shared/utils/excel.utils';
import {
  ProjectSummaryService,
  ProjectSummaryItem,
  ProjectSummaryParams,
} from '../../core/services/project-summary.service';
import {
  CustomerProjectsService,
  CustomerOption,
  ProjectOption,
  ActivityOption,
} from '../../core/services/customer-projects.service';

type GroupMode = 'KU' | 'A';

interface HeaderColumn {
  label: string;
  key: string;
  type: 'string' | 'number' | 'date';
}

function applyWorksheetFormats(
  worksheet: XLSX.WorkSheet,
  headers: HeaderColumn[],
  rowCount: number
): void {
  for (let row = 1; row <= rowCount; row++) {
    headers.forEach((header, col) => {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = worksheet[cellAddress];
      if (!cell || cell.v === '' || cell.v === null || cell.v === undefined) {
        return;
      }

      if (header.type === 'date') {
        const dateParts = String(cell.v).split('-');
        if (dateParts.length === 3) {
          cell.t = 'd';
          cell.v = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]));
          cell.z = 'yyyy-mm-dd';
        }
        return;
      }

      if (header.type === 'number') {
        const numValue = typeof cell.v === 'number' ? cell.v : Number(cell.v);
        if (!Number.isNaN(numValue)) {
          cell.t = 'n';
          cell.v = numValue;
          cell.z = Number.isInteger(numValue) ? '0' : '0.00';
        }
      }
    });
  }
}

@Component({
  selector: 'app-ng-project-summary-reports',
  template: require('./ng-project-summary-reports.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-project-summary-reports.component.scss').default],
  standalone: false,
})
export class NgProjectSummaryReportsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  // Modes
  groupMode: GroupMode = 'KU';
  selMode: 'active' | 'twomonths' | 'all' = 'active';
  custMode = { external: true, internal: false, absense: false };

  // Select options
  customers: CustomerOption[] = [];
  selectedCustomer: CustomerOption | null = null;
  selectedProject: ProjectOption | null = null;
  selectedActivity: ActivityOption | null = null;

  // Data
  reportData: ProjectSummaryItem[] = [];
  headers: HeaderColumn[] = [];

  // Sorting
  private sortAscending = true;

  constructor(
    @Inject(ProjectSummaryService) private projectSummaryService: ProjectSummaryService,
    @Inject(CustomerProjectsService) private customerProjectsService: CustomerProjectsService,
  ) {}

  ngOnInit(): void {
    this.fetchCustomerProjects();
  }

  fetchCustomerProjects(): void {
    this.customerProjectsService
      .getCustomerProjects()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.customers = data;
      });
  }

  onCustomerChange(): void {
    this.selectedProject = null;
    this.selectedActivity = null;
  }

  onProjectChange(): void {
    this.selectedActivity = null;
  }

  get projects(): ProjectOption[] {
    return this.selectedCustomer?.projects ?? [];
  }

  get activities(): ActivityOption[] {
    return this.selectedProject?.activities ?? [];
  }

  fetchReport(): void {
    this.updateHeaders();

    // Default dates (current month) - API seems to require them even if selMode is used
    const dateFrom = moment().startOf('month').format('YYYY-MM-DD');
    const dateTo = moment().endOf('month').format('YYYY-MM-DD');

    const params: ProjectSummaryParams = {
      custMode: this.custMode,
      cust: this.selectedCustomer?.id ?? null,
      proj: this.selectedProject?.id ?? null,
      act: this.selectedActivity?.id ?? null,
      per: 'A',
      type: this.groupMode,
      selection: this.selMode,
      from: dateFrom,
      to: dateTo,
    };

    this.projectSummaryService
      .getProjectSummary(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.reportData = data;
      });
  }

  private updateHeaders(): void {
    const commonHeaders: HeaderColumn[] = [
      { label: 'Kund', key: 'customername', type: 'string' },
      { label: 'Slutkund', key: 'endcustomer', type: 'string' },
      { label: 'Projekt', key: 'project_name', type: 'string' },
      { label: 'Ext projekt', key: 'external_project_name', type: 'string' },
      { label: 'Namn', key: 'name', type: 'string' },
      { label: 'Aktivitet', key: 'activity_name', type: 'string' },
      { label: 'Ext aktivitet', key: 'external_activity_name', type: 'string' },
      { label: 'Start datum', key: 'start_date', type: 'date' },
      { label: 'Slut datum', key: 'stop_date', type: 'date' },
      { label: 'Pris', key: 'price', type: 'number' },
    ];

    if (this.groupMode === 'KU') {
      this.headers = commonHeaders;
    } else {
      // 'A' mode - same headers but different order in legacy?
      // Legacy code has almost identical headers for KU and A.
      // Keeping it simple and consistent.
      this.headers = [
        { label: 'Namn', key: 'name', type: 'string' },
        ...commonHeaders.filter((h) => h.key !== 'name'),
      ];
    }
  }

  sortBy(index: number): void {
    const header = this.headers[index];
    const key = header.key as keyof ProjectSummaryItem;

    this.reportData.sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison: number;
      if (header.type === 'number') {
        comparison = (aVal as number) - (bVal as number);
      } else {
        comparison = String(aVal).localeCompare(String(bVal), 'sv');
      }

      return this.sortAscending ? comparison : -comparison;
    });

    this.sortAscending = !this.sortAscending;
    this.reportData = [...this.reportData];
  }

  getCellValue(item: ProjectSummaryItem, header: HeaderColumn): string | number | null {
    if (header.key === 'name' && item.contractor === 'J') {
      return `${item.name} (underkonsult)`;
    }
    return item[header.key as keyof ProjectSummaryItem] ?? null;
  }

  exportToExcel(): void {
    const exportData = this.reportData.map((item) => {
      const row: Record<string, string | number> = {};
      for (const header of this.headers) {
        const value = this.getCellValue(item, header);
        row[header.label] = value ?? '';
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    removeBlankStringCells(worksheet);
    applyWorksheetFormats(worksheet, this.headers, exportData.length);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Projektrapport');
    XLSX.writeFile(workbook, 'projSummaryReport.xlsx');
  }

  trackByIndex(index: number): number {
    return index;
  }
}
